import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useNavigate } from 'react-router-dom';
import Toast from '../../ui/Toast';
import './TierManagement.css';

const INDIVIDUAL_PLANS = ['Free', 'Pro', 'Premium'];
const ORGANIZATION_PLANS = ['Free', 'Bronze', 'Silver', 'Gold', 'Scale'];

const PLAN_FEATURES = {
  Free: ['20 Credits / month', 'Basic Search', 'Lead Export'],
  // Personal Plan
  Pro: ['500 Credits / month', 'Advanced Search', 'Priority Support'],
  Premium: ['2,000 Credits / month', 'Advanced Search', 'PDF Enrichment', 'Priority Support'],
  // Organization Plan
  Bronze: ['1,000 Credits / month', 'Advanced Search', 'Team Access (3 seats)'],
  Silver: ['5,000 Credits / month', 'Advanced Search', 'PDF Enrichment', 'Team Access (10 seats)'],
  Gold: [
    '15,000 Credits / month',
    'Full API Access',
    'PDF Enrichment',
    'Bulk Prospecting',
    'Team Access (25 seats)',
  ],
  Scale: [
    'Admin Assigned Credits',
    'Priority Support',
    'Team Access (100 seats)',
    'Bulk Prospecting',
  ],
};

const PLAN_BADGE = {
  Pro: 'Popular',
  Silver: 'Best Value',
};

const EMAIL_TO_CONTACT = process.env.REACT_APP_CONTACT_US || 'support@fpgtechnology.com';

function TierManagement({ currentUser, setCurrentUser }) {
  const {
    workspaceName,
    workspaceType,
    workspacePlan,
    workspaceCredits,
    workspaceCreditLimit,
    isOwner,
    isOrganizationAdmin,
    refreshWorkspaceContext,
  } = useWorkspace();
  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState(false);
  const [planChangeRequest, setPlanChangeRequest] = useState(null); // { tier, isUpgrade, currentTier }

  let accountType = currentUser?.account_type;

  const isIndividual = accountType === 'personal';
  const isOrganizationAccount = workspaceType === 'organization' || workspaceType === 'org';
  const plans = isIndividual ? INDIVIDUAL_PLANS : ORGANIZATION_PLANS;
  const freeTier = 'Free';

  // For org admins, show the organization's tier/plan/credits, not their personal profile
  const effectiveTier =
    isOrganizationAccount && (isOwner || isOrganizationAdmin)
      ? workspacePlan || currentUser?.tier
      : currentUser?.tier;
  const effectiveCreditBalance =
    isOrganizationAccount && (isOwner || isOrganizationAdmin)
      ? workspaceCredits !== null && workspaceCredits !== undefined
        ? workspaceCredits
        : currentUser?.creditBalance
      : currentUser?.creditBalance;
  const effectiveCreditLimit =
    isOrganizationAccount && (isOwner || isOrganizationAdmin)
      ? workspaceCreditLimit !== null && workspaceCreditLimit !== undefined
        ? workspaceCreditLimit
        : currentUser?.creditLimit
      : currentUser?.creditLimit;

  // Determine plan level for Upgrade/Downgrade logic
  const getPlanLevel = (tier) => {
    if (!tier || tier === 'Free') return 1;
    const index = plans.indexOf(tier);
    return index !== -1 ? index + 1 : 1;
  };

  const currentLevel = getPlanLevel(effectiveTier);

  useEffect(() => {
    axios
      .get('stripe/prices')
      .then((res) => {
        if (res.data.success) setPricing(res.data.pricing);
        else {
          setPricingError(true);
          setMessage({
            type: 'error',
            text: 'Could not load live prices from Stripe. Please refresh or contact support.',
          });
        }
      })
      .catch(() => {
        setPricingError(true);
        setMessage({
          type: 'error',
          text: 'Could not load live prices from Stripe. Please refresh or contact support.',
        });
      })
      .finally(() => setPricingLoading(false));
  }, []);

  /** Get the pricing entry for a given tier */
  const getPriceEntry = (tier) => {
    if (!pricing) return null;
    const group = isIndividual ? pricing.individual : pricing.organization;
    return group?.[tier] ?? null;
  };

  /** Format cents → "$59" or "Free" or "…" */
  const formatAmount = (tier) => {
    if (tier === 'Scale') return 'Custom';
    if (pricingLoading) return '…';
    if (pricingError) return '—';
    const entry = getPriceEntry(tier);
    if (!entry) return '—';
    if (entry.amount === null || entry.amount === 0) return 'Free';
    return `$${(entry.amount / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  const getCredits = (tier) => {
    if (!pricing) return '…';
    const entry = getPriceEntry(tier);
    return entry?.credit_limit?.toLocaleString() ?? '…';
  };

  const handlePlanAction = (plan) => {
    if (plan === effectiveTier) return;

    if (plan === 'Scale') {
      navigate('/contact-sales');
      return;
    }

    const targetLevel = getPlanLevel(plan);
    const isUpgrade = targetLevel > currentLevel;

    setPlanChangeRequest({
      tier: plan,
      isUpgrade,
      currentTier: effectiveTier || 'Free',
    });
  };

  const confirmPlanChange = async () => {
    if (!planChangeRequest) return;
    const plan = planChangeRequest.tier;
    const isUpgrade = planChangeRequest.isUpgrade;

    setPlanChangeRequest(null);

    // If it's a simple downgrade to free without Stripe history, we use the direct route
    // But for consistency, we try the Stripe route first for all paid plans
    if (plan === freeTier && !currentUser?.stripe_subscription_id) {
      await confirmDowngradeToFree();
      return;
    }

    try {
      setIsProcessing(true);
      const response = await axios.post('stripe/create-checkout-session', {
        tier: plan,
        email: currentUser?.email,
      });

      // Case 1: Subscription updated inline (proration) — no checkout needed
      if (response.data?.upgraded) {
        const profileRes = await axios.get('profile');
        if (profileRes.data?.success) {
          const p = profileRes.data.data;
          setCurrentUser((prev) => ({
            ...prev,
            tier: p.tier || 'Free',
            creditBalance: p.credit_balance || 0,
            creditLimit: p.credit_limit || 0,
          }));
          if (refreshWorkspaceContext) refreshWorkspaceContext();
        }
        setMessage({ type: 'success', text: `Successfully upgraded to ${response.data.tier}!` });
        return;
      }

      // Case 2: Redirect to Stripe Checkout for new subscription
      if (response.data?.url) {
        window.location.href = response.data.url;
      } else {
        setMessage({ type: 'error', text: 'Failed to initiate payment. Please try again.' });
      }
    } catch (error) {
      console.error('[TierManagement] Checkout error:', error.response?.data || error.message);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to initiate payment. Please try again.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  /** Open Stripe Customer Portal for billing management */
  const handleManageBilling = async () => {
    try {
      setIsProcessing(true);
      const response = await axios.post('stripe/create-portal-session');
      if (response.data?.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Could not open billing portal.',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const confirmDowngradeToFree = async () => {
    setIsProcessing(true);
    try {
      await axios.post('profile/tier', { tier: freeTier });
      const response = await axios.get('profile');
      if (response.data?.success) {
        const p = response.data.data;
        setCurrentUser((prev) => ({
          ...prev,
          tier: p.tier || 'Free',
          creditBalance: p.credit_balance || 0,
          creditLimit: p.credit_limit || 0,
          account_type: p.account_type || prev?.account_type,
        }));
        if (refreshWorkspaceContext) refreshWorkspaceContext();
        setMessage({ type: 'success', text: 'Successfully downgraded to Free plan.' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to downgrade plan.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const renderPlanCard = (tier) => {
    const isCurrent = effectiveTier === tier;
    const targetLevel = getPlanLevel(tier);
    const isUpgrade = targetLevel > currentLevel;
    const isDowngrade = targetLevel < currentLevel;

    const isFeatured = tier === 'Pro' || tier === 'Silver';
    const badge = PLAN_BADGE[tier];
    const amount = formatAmount(tier);
    const isFree = amount === 'Free';

    return (
      <div
        key={tier}
        className={`plan-card ${isCurrent ? 'active' : ''} ${isFeatured ? 'featured' : ''}`}
      >
        {badge && <div className="plan-badge">{badge}</div>}
        <div className="plan-header">
          <h3>{tier}</h3>
          <div className="plan-price">
            <div className="price-amount">
              {pricingLoading && tier !== 'Scale' ? (
                <span className="price-loading">…</span>
              ) : isFree ? (
                ''
              ) : (
                amount
              )}
              {!isFree && !pricingLoading && tier !== 'Scale' && (
                <span className="per-month">/month</span>
              )}
            </div>
            <div className="billed-monthly">
              {isFree ? 'Forever free' : tier === 'Scale' ? 'Talk to sales' : 'Billed monthly'}
            </div>
          </div>
        </div>
        <ul className="plan-features">
          {PLAN_FEATURES[tier].map((f, i) => (
            <li key={i}>
              <i className="fas fa-check"></i> {f}
            </li>
          ))}
        </ul>
        <button
          className="plan-btn"
          disabled={isCurrent || (pricingLoading && tier !== 'Scale') || isProcessing}
          onClick={() => handlePlanAction(tier)}
        >
          {isCurrent
            ? 'Current Plan'
            : tier === 'Scale'
              ? 'Contact Sales'
              : isProcessing
                ? 'Processing...'
                : isUpgrade
                  ? `Upgrade to ${tier}`
                  : `Downgrade to ${tier}`}
        </button>
      </div>
    );
  };

  // Allow owners and admins to upgrade, restrict only members
  const isRestrictedUser = isOrganizationAccount && !isOwner && !isOrganizationAdmin;

  return (
    <div className="tier-management-container">
      {/* ── ORG ACCOUNT: read-only billing info, no purchase controls ── */}
      {isRestrictedUser ? (
        <>
          <div className="tier-management-header">
            <h1>Subscription &amp; Plans</h1>
            <p>Credits and subscriptions are managed by your organization admin.</p>
          </div>
          <div className="tier-management-content">
            {/* Show current status read-only */}
            <div className="tier-status-summary">
              <div className="info-stat">
                <label>Organization</label>
                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '15px' }}>
                  {workspaceName || '—'}
                </div>
              </div>
              <div className="info-stat">
                <label>Current Plan</label>
                <div className={`tier-badge ${effectiveTier || 'Free'}`}>
                  {effectiveTier || 'Free'}
                </div>
              </div>
              <div className="info-stat">
                <label>Credit Balance</label>
                <div className="stat-value">
                  {effectiveCreditBalance?.toLocaleString() || 0}
                  <span className="stat-limit">
                    {' '}
                    / {effectiveCreditLimit?.toLocaleString() || 0}
                  </span>
                </div>
                {(() => {
                  const pct = Math.min(
                    100,
                    Math.round(
                      ((effectiveCreditBalance || 0) / Math.max(effectiveCreditLimit || 1, 1)) * 100
                    )
                  );
                  return (
                    <div className="credit-progress-bar-wrap">
                      <div className="credit-progress-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Org-member restriction notice */}
            <div className="org-notice-box">
              <div style={{ fontSize: '28px', lineHeight: 1 }}>🏢</div>
              <div>
                <div
                  style={{
                    fontWeight: '700',
                    color: '#1e40af',
                    fontSize: '15px',
                    marginBottom: '4px',
                  }}
                >
                  Managed by Organization
                </div>
                <div style={{ color: '#3b4a6b', fontSize: '13.5px', lineHeight: '1.6' }}>
                  You are currently on an organization account. Upgrades and credits are managed by
                  your company. Please contact the organization admin or owner to assign more
                  credits.
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        /* ── NORMAL VIEW: full plan grid for admins / personal users ── */
        <>
          <div className="tier-management-header">
            <h1>Subscription &amp; Plans</h1>
            <p>
              You are on {isIndividual ? 'a' : 'an'}&nbsp;
              <span className={`account-type-badge ${accountType}`}>
                {isIndividual ? '👤 Personal' : '🏢 Organization'}
              </span>
              &nbsp;account
            </p>
            <p className="tier-management-subtitle">
              Choose the best plan for your needs. Scale your prospecting with advanced filters, PDF
              enrichment, and team collaboration.
            </p>
          </div>

          <div className="tier-management-content">
            {/* Current status */}
            {!currentUser?.isAdmin && (
              <div className="tier-status-summary">
                <div className="info-stat credit-balance-card" style={{ flex: 1 }}>
                  <label
                    style={{
                      color: '#94a3b8',
                      fontSize: '11px',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: '10px',
                      display: 'block',
                    }}
                  >
                    Credit Balance
                  </label>
                  <div
                    className="stat-value"
                    style={{
                      fontSize: '24px',
                      fontWeight: '800',
                      color: '#0f172a',
                      marginBottom: '10px',
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '4px',
                    }}
                  >
                    {effectiveCreditBalance?.toLocaleString() || 0}
                    <span
                      className="stat-limit"
                      style={{ color: '#94a3b8', fontSize: '18px', fontWeight: '500' }}
                    >
                      {' '}
                      / {effectiveCreditLimit?.toLocaleString() || 0}
                    </span>
                  </div>
                  {(() => {
                    const pct = Math.min(
                      100,
                      Math.round(
                        ((effectiveCreditBalance || 0) / Math.max(effectiveCreditLimit || 1, 1)) *
                          100
                      )
                    );
                    return (
                      <div
                        className="credit-progress-bar-wrap"
                        style={{
                          height: '6px',
                          background: '#f1f5f9',
                          borderRadius: '3px',
                          overflow: 'hidden',
                          width: '100%',
                        }}
                      >
                        <div
                          className="credit-progress-bar-fill"
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            background: '#6366f1',
                            borderRadius: '3px',
                            transition: 'width 0.5s ease',
                          }}
                        />
                      </div>
                    );
                  })()}
                </div>
                {effectiveTier && effectiveTier !== 'Free' && isOwner && (
                  <div className="info-stat manage-billing">
                    <button
                      className="btn-manage-billing"
                      onClick={handleManageBilling}
                      disabled={isProcessing}
                    >
                      <i className="fas fa-credit-card"></i>
                      Manage Billing
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Plan grid */}
            {currentUser?.isAdmin ? (
              <div className="admin-access-message">
                <i className="fas fa-shield-alt"></i>
                <h3>Unlimited System Access</h3>
                <p>
                  As an administrator, you have full access to all platform features without credit
                  restrictions.
                </p>
              </div>
            ) : (
              <>
                <div className="tier-section-label">
                  {isIndividual ? 'Individual Plans' : 'Organization Plans'}
                </div>
                <div className={`tier-plans-grid grid-${plans.length}`}>
                  {plans.map(renderPlanCard)}
                </div>
              </>
            )}

            <Toast message={message?.text} type={message?.type} onClose={() => setMessage(null)} />
          </div>
        </>
      )}{' '}
      {/* end ternary */}
      {/* Plan Change Confirmation Modal */}
      {planChangeRequest && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <div
                className={`warning-icon ${planChangeRequest.isUpgrade ? 'upgrade' : 'downgrade'}`}
              >
                <i
                  className={`fas fa-${planChangeRequest.isUpgrade ? 'arrow-up' : 'exclamation-triangle'}`}
                ></i>
              </div>
              <h2>Confirm {planChangeRequest.isUpgrade ? 'Upgrade' : 'Downgrade'}</h2>
            </div>
            <div className="modal-body">
              <div className="plan-transition-summary">
                <div className="tier-tag current">{planChangeRequest.currentTier}</div>
                <div className="transition-arrow">
                  <i className="fas fa-long-arrow-alt-right"></i>
                </div>
                <div className="tier-tag target">{planChangeRequest.tier}</div>
              </div>

              <p className="modal-description">
                Are you sure you want to {planChangeRequest.isUpgrade ? 'upgrade' : 'downgrade'}{' '}
                your workspace plan?
              </p>

              <div className="modal-info-strip">
                <div className="info-modern-box">
                  <div className="info-icon">
                    <i className="fas fa-info-circle"></i>
                  </div>
                  <div className="info-text">
                    {planChangeRequest.isUpgrade
                      ? 'You will be charged a prorated amount for the remainder of this billing cycle.'
                      : 'This change takes effect immediately. Any unused time will be credited for future use.'}
                  </div>
                </div>

                <div className="limit-highlight">
                  <div className="limit-icon">
                    <i className="fas fa-bolt"></i>
                  </div>
                  <div className="limit-text">
                    New Monthly Credit Limit: <strong>{getCredits(planChangeRequest.tier)}</strong>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setPlanChangeRequest(null)}
                disabled={isProcessing}
              >
                Cancel
              </button>
              <button
                className={`btn-${planChangeRequest.isUpgrade ? 'primary' : 'danger'}`}
                onClick={confirmPlanChange}
                disabled={isProcessing}
              >
                {isProcessing
                  ? 'Processing...'
                  : `Confirm ${planChangeRequest.isUpgrade ? 'Upgrade' : 'Downgrade'}`}
              </button>
            </div>
          </div>
        </div>
      )}
      <div>
        <p>
          Before making any changes to your subscription, please review our{' '}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>
          .
        </p>
        <p>
          If you have any questions or need assistance, please contact our support team at{' '}
          <a href={`mailto:${EMAIL_TO_CONTACT}`}>{EMAIL_TO_CONTACT}</a>
        </p>
      </div>
    </div>
  );
}

export default TierManagement;
